/**
 * Lifecycle primitives for safe teardown and event handling.
 *
 * Solves: Set.forEach() mutation during iteration, unsubscriber arrays
 * corrupted by teardown-triggered re-subscription, and inconsistent
 * cleanup patterns across services.
 *
 * Four primitives:
 * - IDisposable: universal cleanup contract
 * - DisposableStore: collects disposables, snapshot-safe dispose/clear
 * - Emitter<T>: single-event emitter, snapshot-safe fire, returns disposables
 * - EventMap<T>: multi-event emitter (replaces mitt), snapshot-safe, returns disposables
 */

// ─── IDisposable ─────────────────────────────────────────────

export interface IDisposable {
  dispose(): void;
}

/** Wrap a cleanup function as an IDisposable (idempotent). */
export function toDisposable(fn: () => void): IDisposable {
  let disposed = false;
  return {
    dispose() {
      if (disposed) return;
      disposed = true;
      fn();
    },
  };
}

// ─── DisposableStore ─────────────────────────────────────────

/**
 * Collects IDisposable instances and disposes them safely.
 *
 * - `clear()` disposes all collected items (can be reused after).
 * - `dispose()` disposes all and marks the store as permanently disposed.
 * - Both snapshot the collection before iterating, so callbacks that
 *   add/remove disposables during teardown cannot cause infinite loops.
 * - Adding to a disposed store immediately disposes the new item.
 */
export class DisposableStore implements IDisposable {
  private disposables = new Set<IDisposable>();
  private _isDisposed = false;

  get isDisposed(): boolean {
    return this._isDisposed;
  }

  /** Add a disposable. Returns it for chaining. */
  add<T extends IDisposable>(disposable: T): T {
    if (this._isDisposed) {
      disposable.dispose();
      return disposable;
    }
    this.disposables.add(disposable);
    return disposable;
  }

  /** Dispose all items but allow the store to be reused. */
  clear(): void {
    const snapshot = [...this.disposables];
    this.disposables.clear();
    for (const d of snapshot) d.dispose();
  }

  /** Dispose all items and permanently seal the store. */
  dispose(): void {
    if (this._isDisposed) return;
    this._isDisposed = true;
    this.clear();
  }
}

// ─── Emitter ─────────────────────────────────────────────────

/**
 * Type-safe event emitter that is snapshot-safe.
 *
 * - `fire()` snapshots listeners before iterating, so subscribe/unsubscribe
 *   during notification cannot cause infinite loops or skipped listeners.
 * - `on()` returns an IDisposable for easy integration with DisposableStore.
 * - Firing after dispose is a no-op.
 */
export class Emitter<T = void> implements IDisposable {
  private listeners = new Set<(value: T) => void>();
  private _isDisposed = false;

  /** Subscribe to events. Returns a disposable that removes the listener. */
  on(listener: (value: T) => void): IDisposable {
    if (this._isDisposed) return toDisposable(() => {});
    this.listeners.add(listener);
    return toDisposable(() => this.listeners.delete(listener));
  }

  /** Notify all current listeners (snapshot-safe). */
  fire(value: T): void {
    if (this._isDisposed) return;
    const snapshot = [...this.listeners];
    for (const fn of snapshot) {
      try {
        fn(value);
      } catch {
        // Prevent one broken listener from killing the rest.
        // Intentionally silent — callers should guard their own callbacks.
      }
    }
  }

  dispose(): void {
    if (this._isDisposed) return;
    this._isDisposed = true;
    this.listeners.clear();
  }
}

// ─── StatefulService ────────────────────────────────────────

/**
 * Base class for services that expose reactive state to React via useSyncExternalStore.
 *
 * useSyncExternalStore requires getSnapshot() to return a referentially stable value
 * (same Object.is reference) when the underlying data hasn't changed. Violating this
 * causes infinite re-render cascades ("Maximum update depth exceeded").
 *
 * StatefulService makes this impossible by construction:
 * - getState() always returns the cached reference
 * - setState() compares before updating, only fires on real changes
 * - Default shallow comparison works for flat state objects; override stateEquals()
 *   for states with arrays or nested objects
 */
export abstract class StatefulService<T> implements IDisposable {
  protected readonly subscriptions = new DisposableStore();
  private readonly _stateEmitter = new Emitter<void>();
  private _state: T;
  private _dirty = false;
  private _rafId: number | null = null;

  constructor(initialState: T) {
    this._state = initialState;
  }

  /** Returns a stable reference. Only changes when setState() detects a real change. */
  getState(): T {
    return this._state;
  }

  /** Subscribe to state changes. */
  onStateChange(listener: () => void): IDisposable {
    return this._stateEmitter.on(listener);
  }

  /**
   * Update state if different from current. Notifications are batched via
   * requestAnimationFrame so rapid state changes (e.g. from telemetry messages
   * at ~150Hz) produce at most one React commit per frame.
   */
  protected setState(next: T): void {
    if (this.stateEquals(this._state, next)) return;
    this._state = next;
    if (!this._dirty) {
      this._dirty = true;
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        this._dirty = false;
        this._stateEmitter.fire();
      });
    }
  }

  protected stateEquals(prev: T, next: T): boolean {
    if (prev === next) return true;
    if (typeof prev !== 'object' || typeof next !== 'object' || !prev || !next) return false;
    const prevObj = prev as Record<string, unknown>;
    const nextObj = next as Record<string, unknown>;
    const prevKeys = Object.keys(prevObj);
    if (prevKeys.length !== Object.keys(nextObj).length) return false;
    return prevKeys.every(key => Object.is(prevObj[key], nextObj[key]));
  }

  dispose(): void {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this.subscriptions.dispose();
    this._stateEmitter.dispose();
  }
}

// ─── EventMap ───────────────────────────────────────────────

/**
 * Multi-event emitter that replaces mitt. Each event key gets its own
 * snapshot-safe Emitter under the hood.
 *
 * - `on()` returns an IDisposable (no need for manual `.off()`).
 * - `onAny()` fires for every event (replaces mitt's wildcard `'*'`).
 * - `dispose()` tears down all internal emitters.
 */
export class EventMap<T extends Record<string, unknown>> implements IDisposable {
  private emitters = new Map<string, Emitter<unknown>>();
  private wildcardEmitter = new Emitter<{ type: string; event: unknown }>();
  private _isDisposed = false;

  /** Subscribe to a specific event. Returns a disposable. */
  on<K extends keyof T & string>(event: K, listener: (value: T[K]) => void): IDisposable {
    if (this._isDisposed) return toDisposable(() => {});
    let emitter = this.emitters.get(event);
    if (!emitter) {
      emitter = new Emitter<unknown>();
      this.emitters.set(event, emitter);
    }
    return emitter.on(listener as (value: unknown) => void);
  }

  /** Subscribe to all events (wildcard). Replaces mitt's `on('*', handler)`. */
  onAny(listener: (type: string, event: unknown) => void): IDisposable {
    if (this._isDisposed) return toDisposable(() => {});
    return this.wildcardEmitter.on(({ type, event }) => listener(type, event));
  }

  /** Emit an event to all listeners for that key + wildcard listeners. */
  emit<K extends keyof T & string>(event: K, value: T[K]): void {
    if (this._isDisposed) return;
    this.emitters.get(event)?.fire(value);
    this.wildcardEmitter.fire({ type: event, event: value });
  }

  dispose(): void {
    if (this._isDisposed) return;
    this._isDisposed = true;
    for (const emitter of this.emitters.values()) {
      emitter.dispose();
    }
    this.emitters.clear();
    this.wildcardEmitter.dispose();
  }
}
